import { Component, OnInit } from '@angular/core';
import { TutorService } from '../tutor.service';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { Router } from '@angular/router';
import { CONSTANTS } from '../../../../core/constants';
import { GlobalFunctions } from '../../../../core/global-functions';
import { CommonService } from '../../../../core/common.service';

@Component({
  selector: 'app-teaching',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiSelectModule,
    DropdownModule,
    ReactiveFormsModule,
    ToastModule
  ],
  templateUrl: './teaching.component.html',
  styleUrl: './teaching.component.scss',
})
export class TeachingComponent implements OnInit {
  subjects: any;
  selectedSubjects: any;
  profileDetails: any;
  subjectsForm: any = FormGroup;
  constants: any = CONSTANTS;
  isBtnLoading: boolean = false;

  constructor(
    private _globalFunctions: GlobalFunctions,
    private _tutorServices: TutorService,
    private messageService: MessageService,
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.prepareSubjectsForm();
    this.getUserDetails();
    this.getSubjectList();
  }

  getUserDetails() {
    this._commonService.getProfile().subscribe({
      next: (result: any) => {
        if (result && result.success && result.data) {
          this.profileDetails = result.data;
          this._commonService.setProfileData(result.data); // Update local storage
          this.prepareSubjectsForm(result.data);
        }
      },
      error: (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      },
    });
  }

  getSubjectList(search: string = '') {
    this._commonService.getSubjects({ search: search }).subscribe(
      (result: any) => {
        if (result && result?.success) {
          const newSubjects = result?.data?.subjects || [];
          
          // ALWAYS Merge new results with currently selected subjects to keep labels visible
          const selectedIds = this.subjectsForm.get('subjects')?.value || [];
          const currentlySelected = (this.subjects || []).filter((s: any) => 
            selectedIds.includes(s._id) || selectedIds.includes(s.id)
          );
          
          const combined = [...currentlySelected, ...newSubjects];
          // Remove duplicates by ID
          this.subjects = Array.from(new Map(combined.map(item => [item._id || item.id, item])).values());
        } else {
          this._globalFunctions.successErrorHandling(
            result?.message,
            this,
            true,
          );
        }
      },
      (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
      },
    );
  }

  onSubjectFilter(event: any) {
    const query = event.filter || '';
    this.getSubjectList(query);
  }

  removeSubject(subject: any, event: MouseEvent) {
    event.stopPropagation();
    const currentValues = this.subjectsForm.get('subjects')?.value || [];
    const idToRemove = subject.id || subject._id;
    const newValues = currentValues.filter((id: any) => id !== idToRemove);
    this.subjectsForm.get('subjects')?.setValue(newValues);
    this.subjectsForm.get('subjects')?.markAsDirty();
  }

  updateSubjects() {
    if (this.subjectsForm.invalid) {
      this.subjectsForm.markAllAsTouched();
      return;
    }

    // Optimization: If no changes, just navigate to the next step
    if (!this.subjectsForm.dirty) {
      this._router.navigate([
        '/tutor-signup/' + this.constants.tutorLanguagesUrl,
      ]);
      return;
    }

    this.isBtnLoading = true;

    let subjectsObj: any = {
      subject_ids: this.subjectsForm?.value?.subjects,
    };

    this._tutorServices.updateSubjects(subjectsObj).subscribe(
      (result: any) => {
        if (result && result?.success) {
          this.isBtnLoading = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: result.message });
          this._commonService.getProfile(false, true).subscribe(
            (profileResult: any) => {
              if (profileResult && profileResult?.success) {
                this._commonService.setProfileData(profileResult?.data);
                this._router.navigate([
                  '/tutor-signup/' + this.constants.tutorLanguagesUrl,
                ]);
              } else {
                this._globalFunctions.successErrorHandling(
                  profileResult,
                  this,
                  true,
                );
              }
            },
            (error: any) => {
              this._globalFunctions.errorHanding(error, this, true);
            },
          );
        } else {
          this.isBtnLoading = false;
          this._globalFunctions.successErrorHandling(result, this, true);
        }
      },
      (error: any) => {
        this.isBtnLoading = false;
        this._globalFunctions.errorHanding(error, this, true);
      },
    );
  }

  goBack() {
    this._router.navigate(['/tutor-signup/' + this.constants.tutorAboutUrl]);
  }

  prepareSubjectsForm(tempObj: any = {}) {
    const subjectIds = (tempObj?.subjects || []).map((s: any) => s.id || s._id);

    this.subjectsForm = this._formBuilder.group({
      subjects: [subjectIds, [Validators.required, Validators.maxLength(5)]],
    });
  }
}
